/**
 * backgroundJobPoller — React 외부에서 동작하는 백그라운드 AI Job 폴링
 *
 * 페이지 이동으로 SSE 연결이 끊긴 후에도 서버의 Job 상태를 추적한다.
 * Zustand store와 toast를 직접 호출하여 React lifecycle에 의존하지 않는다.
 *
 * 기존 pollAIJob.ts 패턴을 재사용하되:
 * - 폴링 간격 4초 (foreground 3초보다 여유)
 * - 최대 150회 = 10분
 * - 개별 이미지 완료 시 toast 실시간 업데이트
 * - 완료 시 "이미지 보기 →" 액션 링크 toast
 */

import { apiFetch } from '@/lib/api'
import { useAIJobStore } from '@/stores/aiJobStore'
import { useUIStore } from '@/stores/uiStore'
import { queryClient } from '@/lib/queryClient'

const POLL_INTERVAL = 4_000
const MAX_POLLS = 150

/** 서버 Job 응답 타입 (GET /api/ai/jobs/:id) — pollAIJob.ts와 동일 */
interface JobResponse {
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'PARTIAL' | 'FAILED' | 'CANCELLED'
  totalImages: number
  completedImages: number
  failedImages: number
  generations: Array<{
    id: string
    generatedUrls: string[] | null
    status: string
  }>
}

const TERMINAL_STATUSES = new Set(['COMPLETED', 'PARTIAL', 'FAILED', 'CANCELLED'])

interface PollerState {
  timerId: ReturnType<typeof setTimeout> | null
  pollCount: number
  /** 진행 중 표시용 toast id (수동 관리) */
  progressToastId: string | null
  lastReportedCount: number
}

const activePollers = new Map<string, PollerState>()

export function startBackgroundPolling(jobId: string): void {
  if (!jobId || jobId === 'undefined') return
  if (activePollers.has(jobId)) return

  const state: PollerState = {
    timerId: null,
    pollCount: 0,
    progressToastId: null,
    lastReportedCount: 0,
  }
  activePollers.set(jobId, state)

  async function poll() {
    const poller = activePollers.get(jobId)
    if (!poller) return

    poller.pollCount++
    if (poller.pollCount > MAX_POLLS) {
      useAIJobStore.getState().updateJob(jobId, {
        status: 'FAILED',
        error: '백그라운드 폴링 타임아웃: 10분 내에 생성이 완료되지 않았습니다',
      })
      cleanupProgressToast(poller)
      useUIStore.getState().addToast({
        type: 'error',
        message: '생성 시간이 초과되었습니다',
        duration: 5000,
      })
      stopBackgroundPolling(jobId)
      return
    }

    try {
      const res = await apiFetch<JobResponse>(`/api/ai/jobs/${jobId}`)

      if (!res.success) {
        // 서버 에러 — 다음 폴링에서 재시도
        poller.timerId = setTimeout(poll, POLL_INTERVAL)
        return
      }

      const { status, totalImages, completedImages, generations } = res.data
      const completedGenerations = generations.filter(
        (g) => g.generatedUrls && g.generatedUrls.length > 0
      )
      const allUrls = completedGenerations.flatMap((g) => g.generatedUrls ?? [])

      // store 업데이트
      useAIJobStore.getState().updateJob(jobId, {
        status,
        totalImages,
        completedImages,
        generatedUrls: allUrls,
      })

      // 진행 중 toast 업데이트 (카운팅)
      if (!TERMINAL_STATUSES.has(status) && totalImages > 0) {
        if (!poller.progressToastId) {
          poller.progressToastId = useUIStore.getState().addToast({
            type: 'info',
            message: `AI 사진 생성 중... ${completedImages}/${totalImages}장 완료`,
            duration: 0, // 수동 관리
          })
        } else if (completedImages > poller.lastReportedCount) {
          useUIStore.getState().updateToast(poller.progressToastId, {
            message: `AI 사진 생성 중... ${completedImages}/${totalImages}장 완료`,
          })
        }
        poller.lastReportedCount = completedImages
      }

      // terminal 상태 처리
      if (TERMINAL_STATUSES.has(status)) {
        cleanupProgressToast(poller)

        const isSuccess = status === 'COMPLETED' || status === 'PARTIAL'
        if (isSuccess) {
          useUIStore.getState().addToast({
            type: 'success',
            message: `AI 사진 ${allUrls.length}장이 생성되었습니다!`,
            duration: 10000,
            action: { label: '이미지 보기 →', href: '/studio/ai' },
          })
        } else {
          const errMsg =
            status === 'FAILED'
              ? 'AI 사진 생성에 실패했습니다'
              : 'AI 사진 생성이 취소되었습니다'
          useAIJobStore.getState().updateJob(jobId, { error: errMsg })
          useUIStore.getState().addToast({
            type: 'error',
            message: errMsg,
            duration: 5000,
          })
        }

        // generations 쿼리 무효화 → ResultGallery 등에서 새 데이터 로드
        queryClient.invalidateQueries({ queryKey: ['generations'] })
        stopBackgroundPolling(jobId)
        return
      }

      // 다음 폴링
      poller.timerId = setTimeout(poll, POLL_INTERVAL)
    } catch {
      // 네트워크 에러 — 재시도
      poller.timerId = setTimeout(poll, POLL_INTERVAL)
    }
  }

  // 첫 폴링 시작
  state.timerId = setTimeout(poll, POLL_INTERVAL)
}

export function stopBackgroundPolling(jobId: string): void {
  const poller = activePollers.get(jobId)
  if (poller) {
    if (poller.timerId !== null) clearTimeout(poller.timerId)
    activePollers.delete(jobId)
  }
}

export function stopAllBackgroundPolling(): void {
  for (const [jobId] of activePollers) {
    stopBackgroundPolling(jobId)
  }
}

export function isBackgroundPolling(jobId: string): boolean {
  return activePollers.has(jobId)
}

function cleanupProgressToast(poller: PollerState): void {
  if (poller.progressToastId) {
    useUIStore.getState().removeToast(poller.progressToastId)
    poller.progressToastId = null
  }
}
